import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import usePermissions from '../../hooks/usePermissions';
import LeaguesTabs from './LeaguesTabs';
import './Leagues.css';

/**
 * Game modes a league can be created for. Labels resolved via t() at render time
 * (module-level constant can't call hooks).
 */
const CREATE_MODES = [
  { key: 'duel', labelKey: 'leagues.createModeDuel' },
  { key: 'wingman', labelKey: 'leagues.createModeWingman' },
  { key: 'competitive', labelKey: 'leagues.createModeComp' },
];

/** Series length a league's matches default to. */
const MATCH_TYPES = [
  { key: 'BO1', labelKey: 'createMatch.bestOf1' },
  { key: 'BO3', labelKey: 'createMatch.bestOf3' },
  { key: 'BO5', labelKey: 'createMatch.bestOf5' },
];

/**
 * Game-mode filter pills shown above the league hub. The empty key means
 * "all modes" and skips the query-string filter. Only the "all" label is
 * translated; the technical mode pills (1v1/2v2/5v5) are literal.
 */
const MODE_FILTERS = [
  { key: '', labelKey: 'matches.all' },
  { key: 'duel', label: '1v1' },
  { key: 'wingman', label: '2v2' },
  { key: 'competitive', label: '5v5' },
];

/**
 * Map a season status to a CSS modifier class for its colored pill.
 *
 * @param {string} status - Season status from the API (upcoming/active/finished).
 * @returns {string} The modifier class name suffix.
 */
function seasonStatusClass(status) {
  if (status === 'active') return 'is-active';
  if (status === 'finished') return 'is-finished';
  return 'is-upcoming';
}

/**
 * Public league hub: lists leagues with a game-mode filter. Each card links to
 * the league detail page where the current season standings are shown.
 */
export default function LeagueList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getAuthHeaders, isLoggedIn } = useAuth();
  const { canManageLeagues } = usePermissions();
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('');

  // Create-league form (admins only).
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newMode, setNewMode] = useState('competitive');
  const [newMatchType, setNewMatchType] = useState('BO1');
  const [newDesc, setNewDesc] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  const submitCreate = async () => {
    setCreateError('');
    if (!newName.trim()) {
      setCreateError(t('leagues.nameRequired'));
      return;
    }
    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/api/leagues`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newName.trim(),
          game_mode: newMode,
          match_type: newMatchType,
          description: newDesc.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || data.detail || t('leagues.createFailed'));
      navigate(`/leagues/${data.id}`);
    } catch (e) {
      setCreateError(e.message || t('auth.genericError'));
      setCreating(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    const url = mode
      ? `${API_URL}/api/leagues?game_mode=${mode}`
      : `${API_URL}/api/leagues`;
    fetch(url)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setLeagues(Array.isArray(data) ? data : []))
      .catch(() => setLeagues([]))
      .finally(() => setLoading(false));
  }, [mode]);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loader" />
      </div>
    );
  }

  return (
    <div className="league-page">
      <div className="league-page-header">
        <div className="league-detail-titles">
          <h1 className="league-page-title">{t('nav.leagues')}</h1>
          {isLoggedIn && canManageLeagues && (
            <button className="btn-primary-sm" onClick={() => setCreateOpen((v) => !v)}>
              {createOpen ? t('matchDetail.cancel') : `+ ${t('leagues.createLeague')}`}
            </button>
          )}
        </div>
        <div className="league-filters">
          {MODE_FILTERS.map((f) => (
            <button
              key={f.key || 'all'}
              onClick={() => setMode(f.key)}
              className={`league-filter-btn ${mode === f.key ? 'active' : ''}`}
            >
              {f.labelKey ? t(f.labelKey) : f.label}
            </button>
          ))}
        </div>
      </div>

      <LeaguesTabs />

      {createOpen && (
        <div className="league-create-form">
          {createError && <div className="league-form-error">{createError}</div>}
          <div className="admin-form-grid">
            <label className="form-field">
              <span className="form-label">{t('leagues.nameLabel')}</span>
              <input className="form-input" value={newName} maxLength={200}
                onChange={(e) => setNewName(e.target.value)} placeholder={t('leagues.namePlaceholder')} />
            </label>
            <label className="form-field">
              <span className="form-label">{t('leagues.modeLabel')}</span>
              <select className="form-input" value={newMode} onChange={(e) => setNewMode(e.target.value)}>
                {CREATE_MODES.map((m) => <option key={m.key} value={m.key}>{t(m.labelKey)}</option>)}
              </select>
            </label>
            <label className="form-field">
              <span className="form-label">{t('leagues.matchLengthLabel')}</span>
              <select className="form-input" value={newMatchType} onChange={(e) => setNewMatchType(e.target.value)}>
                {MATCH_TYPES.map((m) => <option key={m.key} value={m.key}>{t(m.labelKey)}</option>)}
              </select>
            </label>
            <label className="form-field league-create-desc">
              <span className="form-label">{t('leagues.descLabel')}</span>
              <input className="form-input" value={newDesc} maxLength={280}
                onChange={(e) => setNewDesc(e.target.value)} placeholder={t('leagues.descPlaceholder')} />
            </label>
            <button className="btn-primary-sm admin-form-submit" disabled={creating} onClick={submitCreate}>
              {creating ? t('leagues.creating') : t('leagues.createLeague')}
            </button>
          </div>
        </div>
      )}

      {leagues.length === 0 ? (
        <div className="league-empty">{t('leagues.noLeagues')}</div>
      ) : (
        <div className="league-list">
          {leagues.map((lg) => {
            const season = lg.current_season;
            return (
              <div
                key={lg.id}
                onClick={() => navigate(`/leagues/${lg.id}`)}
                className="league-card lift-card notched-card"
              >
                <div className="league-card-main">
                  <div className="league-card-name">{lg.name}</div>
                  <div className="league-card-meta">
                    <span className="league-mode-badge">{lg.game_mode_display}</span>
                    <span>{lg.is_team_based ? t('createMatch.teams') : t('leagues.individual')}</span>
                    <span>{lg.season_count === 1
                      ? t('leagues.seasonsCountOne', { count: lg.season_count })
                      : t('leagues.seasonsCountMany', { count: lg.season_count })}</span>
                  </div>
                </div>
                <div className="league-card-season">
                  {season ? (
                    <>
                      <div className={`league-season-pill ${seasonStatusClass(season.status)}`}>
                        {season.status_display || season.status}
                      </div>
                      <div className="league-card-subtle">
                        {season.name} · {t('leagues.participants', { count: season.participant_count })}
                      </div>
                    </>
                  ) : (
                    <div className="league-card-subtle">{t('leagues.noSeasonStarted')}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
